import { ColDef, RowSelectedEvent } from '@ag-grid-community/core';
import { AgGridReact } from '@ag-grid-community/react';
import { Box, Button, Flex, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhysicianDto, api } from '../../../lib/api';
import { Tag } from '@/components/ui/tag';
import { Grid } from '@/components/ui/grid';
import { formatPhone } from '@/components/ui/phone';
import { PhysicianModal } from '@/components/modal/physician';
import { GridFilterActionBar } from '@/components/ui/grid-filter-bar';
import { EMPTY_GRID_STATE, usePersistedGridState } from '@/hooks/usePersistedGridState';

const defaultColumnDef: ColDef<PhysicianDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

export const PhysiciansPage = () => {
    const queryClient = useQueryClient();
    const { open: isPhysicianModalOpen, onOpen: onPhysicianModalOpen, onClose: onPhysicianModalClose } = useDisclosure();
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const gridRef = useRef<AgGridReact<PhysicianDto>>(null);
    const { gridState, saveGridState, applyGridState } = usePersistedGridState('physiciansGrid', EMPTY_GRID_STATE);

    const query = useQuery<PhysicianDto[]>({
        queryKey: ['physician', 'list'],
        queryFn: async () => {
            const { data } = await api.physician.list();
            return data;
        },
    });

    const [colDefs] = useState<ColDef<PhysicianDto>[]>([
        {
            field: 'npi',
            headerName: 'NPI',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
            pinned: 'left',
            lockPinned: true,
        },
        {
            field: 'firstName',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
            sort: 'asc',
        },
        { field: 'lastName', maxWidth: 140, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
        {
            field: 'credential',
            headerName: 'Credentials',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
        },
        {
            field: 'pecos',
            headerName: 'PECOS',
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: false,
            maxWidth: 140,
            cellRenderer: ({ data }: { data: PhysicianDto }) => {
                return (
                    <Flex alignItems="center" height="100%">
                        <Tag colorPalette={data.pecos ? 'green' : 'red'} size="sm" textTransform="uppercase" fontWeight="700">
                            {data.pecos ? 'enrolled' : 'not enrolled'}
                        </Tag>
                    </Flex>
                );
            },
        },
        { field: 'city', maxWidth: 140, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
        {
            field: 'phone',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
            valueFormatter: ({ value }) => {
                if (!value) {
                    return 'n/a';
                }

                return formatPhone(value) || 'uk';
            },
        },
        {
            field: 'fax',
            flex: 1,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: true,
            valueFormatter: ({ value }) => {
                if (!value) {
                    return 'n/a';
                }

                return formatPhone(value) || 'uk';
            },
        },
    ]);

    const handleAdd = async () => {
        await queryClient.invalidateQueries({ queryKey: ['physician', 'list'] });
        onPhysicianModalClose();
    };

    const handleSelect = (event: RowSelectedEvent) => {
        if (event.node.isSelected()) {
            navigate(`/settings/physicians/${event.node.data.id}/edit`);
        }
    };

    return (
        <>
            <Flex px={{ base: '16px', md: 0 }} h="100%" flexDirection="column" gap="3">
                <Flex>
                    <Button size="sm" onClick={() => onPhysicianModalOpen()} textDecoration="none">
                        Add Physician
                    </Button>
                </Flex>
                <Box style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                    <GridFilterActionBar gridRef={gridRef} filterModel={gridState.filterModel} colDefs={colDefs} />
                    <Grid
                        countLabel="physician"
                        key={isMobile ? 'mobile-grid' : 'grid'}
                        gridRef={gridRef}
                        rowSelection="single"
                        columnDefs={colDefs}
                        rowData={query.data}
                        suppressCellFocus
                        defaultColDef={defaultColumnDef}
                        autoSizeStrategy={isMobile ? { type: 'fitCellContents' } : { type: 'fitGridWidth' }}
                        getRowHeight={isMobile ? () => 60 : undefined}
                        onRowSelected={handleSelect}
                        onGridReady={(event) => applyGridState(event.api)}
                        onFilterChanged={(event) => saveGridState(event.api)}
                        onSortChanged={(event) => saveGridState(event.api)}
                    />
                </Box>
            </Flex>

            <PhysicianModal isOpen={isPhysicianModalOpen} onClose={onPhysicianModalClose} onAdd={() => handleAdd()} />
        </>
    );
};
